import { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import { selectApi } from '@/modules/calendar/api';
import { NewsEvent } from '@/modules/calendar/model/calendar/news-event';
import styles from './data-download.module.css';

export default function DataDownload() {
  const [startDate, setStartDate] = useState<Date>(dayjs().toDate());
  const [endDate, setEndDate] = useState<Date>(dayjs().add(1, 'week').toDate());
  const [downloading, setDownloading] = useState<boolean>(false);

  const datesValid = () => {
    return startDate.getTime() < endDate.getTime();
  };

  const extractCSVChunk = (value: NewsEvent[]): string => {
    const sanitize = (input: any) => {
      if (input === null || input === undefined) input = '';

      if (input === '-Infinity') input = 'N/A';

      return `"${`${input}`.replaceAll('"', '\\"')}"`;
    };

    let csv = '';

    value.forEach(dataRelease => {
      dataRelease.dataSeriesEntries.forEach(series => {
        csv += `${sanitize(dataRelease.date)},\
                           ${sanitize(dataRelease.title)},\
                           ${sanitize(dataRelease.country)},\
                           ${sanitize(series.dataSeriesId)},\
                           ${sanitize(series.display)},\
                           ${sanitize(series.previous)},\
                           ${sanitize(series.forecast()?.value)},\
                           ${sanitize(series.actual)},\
                           ${sanitize(series.type)},\
                           ${sanitize(series.scale)}\n`;
      });
    });

    return csv;
  };

  const downloadCSV = (csv: string) => {
    const blob = new Blob([csv], { type: 'text/csv' });

    // Creating an object for downloading url
    const url = URL.createObjectURL(blob);

    // Creating an anchor(a) tag of HTML
    const a = document.createElement('a');

    // Passing the blob downloading url
    a.setAttribute('href', url);

    // Setting the anchor tag attribute for downloading
    // and passing the download file name
    a.setAttribute(
      'download',
      `download.${dayjs(startDate).format('MM-DD-YYYY')}.${dayjs(endDate).format(
        'MM-DD-YYYY',
      )}.csv`,
    );

    // Performing a download with click
    a.click();
  };

  const downloadData = () => {
    if (downloading) return;

    setDownloading(true);

    const times = [
      {
        startDate: startDate.getTime(),
        endDate: endDate.getTime(),
      },
    ];

    const _30_DAYS = 2_592_000_000;

    if (endDate.getTime() - startDate.getTime() > _30_DAYS) {
      times.pop();

      let end = endDate.getTime();
      let start = end - _30_DAYS;
      const finalStart = startDate.getTime();

      while (end > finalStart) {
        times.push({
          startDate: Math.max(start, finalStart),
          endDate: end,
        });

        end = start;
        start = start - _30_DAYS;
      }
    }

    const promises = times.map(time => selectApi.getCalendar(time.startDate, time.endDate));

    Promise.all(promises)
      .then(value => value.map(extractCSVChunk).reduce((a, b) => a + b, ''))

      .then(downloadCSV)
      .finally(() => {
        setDownloading(false);
      });
  };

  return (
    <>
      <div className={'row mb-3'}>
        <div className={'col-sm mb-2'}>
          <label className={styles.fieldLabel}>Start Date</label>
          <div>
            <ReactDatePicker
              className={'form-control w-100'}
              wrapperClassName={'w-100'}
              selected={startDate}
              onChange={date => setStartDate(dayjs(date).toDate())} //only when value has changed
            />
          </div>
        </div>
        <div className={'col-sm mb-2'}>
          <label className={styles.fieldLabel}>End Date</label>

          <div>
            <ReactDatePicker
              className={'form-control'}
              wrapperClassName={'w-100'}
              selected={endDate}
              onChange={date => setEndDate(dayjs(date).toDate())} //only when value has changed
            />
          </div>
        </div>
      </div>
      {datesValid() || (
        <div className={'row mb-3'}>
          <div className={'col text-danger-light'}>
            Start must be before end date. Max range is three months.
          </div>
        </div>
      )}
      <div className={'row'}>
        <div className={'col'}>
          <button
            className="btn btn-sm btn-tertiary text-base-light"
            disabled={!datesValid() || downloading}
            onClick={downloadData}
          >
            {downloading ? (
              <>
                <Spinner size={'sm'} /> Downloading....
              </>
            ) : (
              'Download data'
            )}
          </button>
        </div>
      </div>
    </>
  );
}
